<template>
  <v-container fluid class="pa-0">
    <v-hover v-slot="{ hover }">
      <v-navigation-drawer mobile-breakpoint="600" app clipped v-model="navDrawer">
        <v-list expand dense>
          <v-card class="d-flex align-center" flat>
            <v-list-item dense @click="goToFolder(computedFolderId)">
              <v-list-item-icon class="mr-0">
                <v-icon small>mdi-arrow-left</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title> Back to folder</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-tooltip v-if="hover || $vuetify.breakpoint.smAndDown" bottom>
              <template #activator="{ on }">
                <v-btn icon v-on="on" @click="navDrawer = false">
                  <v-icon>mdi-chevron-double-left</v-icon>
                </v-btn>
              </template>
              <span>Close side menu</span>
            </v-tooltip>
          </v-card>
          <v-list-item class="mb-2">
            <v-list-item-content>
              <SelectCase
                v-model="case_id"
                :folderId="computedFolderId"
                flat
                hide-details
                hide-create
                @save-as="saveCaseAs"
              ></SelectCase>
            </v-list-item-content>
          </v-list-item>
          <v-list-item
            :to="{ name: 'CaseInput', hash: '#Settings', params: { folderId: computedFolderId } }"
          >
            <v-list-item-title>Settings</v-list-item-title>
          </v-list-item>
          <v-list-group v-if="isRequired('Inputs')" no-action :value="true">
            <template #activator>
              <v-list-item-content>
                <v-list-item-title>Inputs</v-list-item-title>
              </v-list-item-content>
            </template>
            <template v-for="page in inputPages">
              <v-list-item
                v-if="!page.children"
                :key="page.name"
                :to="{
                  name: 'CaseInput',
                  hash: hashify(page.name),
                  params: { folderId: computedFolderId },
                }"
                class="pl-8"
              >
                <v-list-item-content>
                  <v-list-item-title>{{ page.name }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-group v-else :key="page.name">
                <template #activator>
                  <v-list-item-title class="pl-4">{{ page.name }}</v-list-item-title>
                </template>
                <v-list-item
                  v-for="child in page.children"
                  :key="child.name"
                  :to="{
                    name: 'CaseInput',
                    hash: hashify(child.name),
                    params: { folderId: computedFolderId },
                  }"
                  class="pl-12"
                >
                  <v-list-item-content>
                    <v-list-item-title>{{ child.name }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list-group>
            </template>
          </v-list-group>
          <v-list-group no-action>
            <template #activator>
              <v-list-item-content>
                <v-list-item-title>Views</v-list-item-title>
              </v-list-item-content>
            </template>
            <v-list-item
              v-for="page in viewsPages"
              :key="page.name"
              class="pl-8"
              :to="{
                name: 'CaseVis',
                hash: hashify(page.name),
                params: { folderId: computedFolderId },
              }"
            >
              <v-list-item-content>
                <v-list-item-title>{{ page.name }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list-group>
          <v-list-item
            :to="{ name: 'CaseOutput', params: { folderId: computedFolderId } }"
            :disabled="!jobs.length || jobs.length == getJobsInProgress.length"
          >
            <v-list-item-title>Outputs</v-list-item-title>
          </v-list-item>
          <v-list-group no-action :value="true">
            <template #activator>
              <v-list-item-title>Calculations</v-list-item-title>
              <v-list-item-action v-if="jobLoadPending && !isJobInProgress" class="my-0">
                <v-progress-circular indeterminate color="secondary"></v-progress-circular>
              </v-list-item-action>
              <v-list-item-action v-else-if="isJobInProgress" class="my-0">
                <v-tooltip top>
                  <template #activator="{ on }">
                    <span class="text-caption">Stop Jobs</span>
                    <v-btn icon @click.stop="cancelJobsInProgress()" v-on="on">
                      <v-icon>cancel</v-icon>
                    </v-btn>
                  </template>
                  <span>Stop Jobs In Progress</span>
                </v-tooltip>
              </v-list-item-action>
              <v-list-item-action v-else-if="hasLogReports" class="my-0">
                <MenuMore offset-x offset-y origin="top left">
                  <v-list dense>
                    <v-list-item
                      v-for="job in jobsWithLogReports"
                      :key="'log-report-' + job.type"
                      @click="downloadLogReport(job.report_url)"
                    >
                      <v-list-item-icon>
                        <v-icon>get_app</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        {{ calculations.find(({ type }) => type == job.type).name }} Log Reports
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </MenuMore>
              </v-list-item-action>
              <!-- <v-list-item-action v-else @click="loadJobs">
            <v-icon>refresh</v-icon>
            </v-list-item-action>-->
            </template>

            <template v-if="currentCase">
              <v-list-item
                v-for="page in computedCalculations"
                :key="page.name"
                :disabled="!jobStartable(page) || jobLoadPending || currentCase.locked"
                class="pl-8"
              >
                <v-list-item-content>
                  <v-list-item-title>{{ page.name }}</v-list-item-title>
                  <v-list-item-subtitle v-if="getJob(page.type)">{{
                    new Date(getJob(page.type).created).toLocaleString()
                  }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action class="my-0">
                  <JobIndicator
                    :status="getJobStatus(page.type)"
                    :progress="getJobProgress(page.type)"
                    :error="getJobError(page.type)"
                    @click="handleClickJob(page.type)"
                  />
                </v-list-item-action>
              </v-list-item>
            </template>
          </v-list-group>
          <MessageList
            v-if="currentCase"
            :currentCase="currentCase"
            :jobInProgress="isJobInProgress"
          ></MessageList>
        </v-list>
      </v-navigation-drawer>
    </v-hover>

    <keep-alive>
      <transition>
        <router-view
          :currentCase="currentCase"
          :errors="currentCaseErrors"
          :num_years="num_years"
          :has-changed="hasChanged"
          :drawer-state="navDrawer"
          @toggle-drawer="navDrawer = !navDrawer"
          @change="caseChanged"
          @save="confirmSaveCase"
          @remove="removeCase"
        ></router-view>
      </transition>
    </keep-alive>

    <v-dialog v-model="reRunDialog" max-width="600">
      <v-card>
        <v-card-title class="text-h6 warning mb-4"> <v-icon>warning</v-icon>Warning! </v-card-title>
        <v-card-text class="text-body-1">
          {{
            `You will delete current outputs! Are you sure you want to run ${mapTypeName(
              currentType
            )}
          again?`
          }}
        </v-card-text>
        <v-card-text class="text-body-1">{{
          currentType === 1 && aaAndPCComplete ? "Production Cost Outputs will be invalid." : ""
        }}</v-card-text>
        <v-card-actions>
          <v-btn @click="reRunDialog = false" text color="red darken-1"> Cancel </v-btn>
          <v-spacer />
          <v-btn color="primary" text @click="reRunJob(currentType)">
            {{ "Run " + `${mapTypeName(currentType)}` }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="warningsDialog" max-width="800">
      <WarningsDialog
        :type="currentType"
        :warnings="currentWarnings"
        :typeName="mapTypeName(currentType)"
        @close="warningsDialog = false"
        @re-run="reRunJob"
      />
    </v-dialog>

    <v-dialog v-model="invalidCaseDialog" max-width="600">
      <v-card>
        <v-alert type="error" tile> Error! </v-alert>
        <v-card-text class="pt-2">
          This case has errors! Are you sure you want to save?
          <v-list dense>
            <v-list-item>
              <v-list-item-title> Errors (total: {{ caseErrors.length }}): </v-list-item-title>
            </v-list-item>
            <v-list-item v-for="(error, i) in dialogErrors" :key="i" class="error-item">
              <v-list-item-subtitle>
                {{ parseError(error) }}
              </v-list-item-subtitle>
            </v-list-item>
            <v-list-item v-if="caseErrors.length > 5">
              <v-tooltip top>
                <template #activator="{ on }">
                  <p
                    class="text-clickable text-caption"
                    v-on="on"
                    @click="showAllErrors = !showAllErrors"
                  >
                    {{ showAllErrors ? "Collapse" : "..." }}
                  </p>
                </template>
                <span>{{ showAllErrors ? "Collapse items" : "Show all" }}</span>
              </v-tooltip>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-btn color="green darken-1" text @click="invalidCaseDialog = false"> Nevermind </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="green darken-1" text @click="confirmSaveCase" :loading="caseSavePending">
            Save Anyways
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="confirmSaveDialog" persistent max-width="400">
      <v-card>
        <v-card-title class="text-h5 warning"> <v-icon>warning</v-icon>Warning! </v-card-title>
        <v-card-text>
          This case has outputs that will be deleted if you save this case.
          <br />Would you like to save your changes as a new case?
          <v-text-field v-model="newCaseName" label="New Case" type="text" hide-details>
            <template #append-outer>
              <v-btn @click="saveCaseAs(newCaseName)">Save As</v-btn>
            </template>
          </v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-btn color="green darken-1" text @click="confirmSaveDialog = false">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="green darken-1" text @click="saveCase" :loading="caseSavePending">
            Save Anyways
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>
<script>
import Ajv from "ajv";
import { CaseModel, runUpdates, schemas } from "data-dictionary";
import { makeFindMixin } from "feathers-vuex";
import pointer from "json-pointer";
import { mapActions, mapGetters, mapState } from "vuex";
import WarningsDialog from "../components/WarningsDialog.vue";
import calculationsMixin from "../mixins/calculationsMixin";
import inputSelectionMixin from "../mixins/inputSelectionMixin";

export default {
  name: "CaseView",
  components: {
    SelectCase: () => import("@/components/SelectCase.vue"),
    JobIndicator: () => import("@/components/JobIndicator.vue"),
    MessageList: () => import("@/components/MessageList.vue"),
    MenuMore: () => import("@/components/MenuMore.vue"),
    WarningsDialog,
  },
  mixins: [
    makeFindMixin({
      service: "jobs",
      watch: true,
    }),
    calculationsMixin,
    inputSelectionMixin,
  ],
  props: {
    scenario: {
      type: Number,
      required: false,
      default: null,
    },
    folderId: {
      type: String,
      required: false,
      default: null,
    },
    id: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      ajv: new Ajv({
        schemas,
        allErrors: true,
        coerceTypes: true,
        jsonPointers: true,
      }),
      jobTypes: new Map([
        [
          1,
          {
            shortName: "AA",
            longName: "Alternative Analysis",
            enumName: "ALTERNATIVE_ANALYSIS",
          },
        ],
        [
          2,
          {
            shortName: "PC",
            longName: "Production Cost",
            enumName: "PRODUCTION_COST",
          },
        ],
        [
          10,
          {
            shortName: "Nodal PC",
            longName: "Nodal Production Cost",
            enumName: "PRODUCTION_COST",
          },
        ],
        [
          6,
          {
            shortName: "ELCC",
            longName: "ELCC",
            enumName: "ELCC",
          },
        ],
        [
          7,
          {
            shortName: "SSE",
            longName: "Stacked Services Emulator",
            enumName: "STACKED_SERVICES_EMULATOR",
          },
        ],
        [
          8,
          {
            shortName: "PF",
            longName: "Power Flow",
            enumName: "POWER_FLOW",
          },
        ],
        [
          9,
          {
            shortName: "SC",
            longName: "Short Circuit",
            enumName: "SHORT_CIRCUIT",
          },
        ],
        [
          12,
          {
            shortName: "TS",
            longName: "Transient Stability",
            enumName: "TRANSIENT_STABILITY",
          },
        ],
        [
          13,
          {
            shortName: "LF",
            longName: "Load Forecast",
            enumName: "LOAD_FORECAST",
          },
        ],
      ]),

      currentCase: null,
      hasChanged: false,
      currentType: null,
      reRunDialog: null,
      warningsDialog: null,
      currentWarnings: null,
      confirmSaveDialog: null,
      invalidCaseDialog: null,
      newCaseName: "",

      caseErrors: [],

      outputPoll: null,

      navDrawer: null,
      navDrawerInput: true,
      navDrawerCalculations: true,
      pages: [
        {
          name: "Network",
        },
        {
          name: "Resources",
          children: [
            {
              name: "Demand",
            },
            {
              name: "Generators",
            },
            {
              name: "Energy Storage",
            },
            {
              name: "Market",
            },
          ],
        },
        {
          name: "Policies",
        },
        {
          name: "Reliability Criteria",
        },
        {
          name: "Project",
        },
      ],
      viewsPages: [{ name: "Map" }, { name: "Network Diagram" }],
      showAllErrors: false,
      refreshJobs: null,
    };
  },

  computed: {
    ...mapState({}),
    ...mapGetters({
      getCase: "cases/get",
      caseSavePending: "cases/savePending",
      jobLoadPending: "jobs/loadPending",
    }),
    jobsParams() {
      return {
        paginate: false,
        query: { case_id: this.case_id },
      };
    },
    computedCalculations() {
      let calculationTypes = this.calculations;
      calculationTypes = calculationTypes.filter((e) => this.currentCase.settings[e.key].active);
      return calculationTypes.map(({ name, key, type }) => ({
        name,
        value: key,
        type,
        job: this.getJob(type),
      }));
    },
    num_years() {
      if (this.currentCase) {
        const { year_start: startYear, year_end: endYear } = this.currentCase.settings;
        return endYear - startYear + 1;
      }
      return null;
    },
    scenario_id: {
      get() {
        return this.scenario || null;
      },
      set(s) {
        this.$router.push({
          name: this.$route.name,
          params: { scenario: s, id: this.case_id },
          hash: this.$route.hash,
        });
      },
    },
    computedFolderId: {
      get() {
        return this.folderId || null;
      },
      set(f) {
        this.$router.replace({
          name: this.$route.name,
          params: { id: this.case_id, folderId: f },
          query: this.$route.query,
          hash: this.$route.hash,
        });
      },
    },
    case_id: {
      get() {
        return this.id;
      },
      set(c) {
        this.$router.replace({
          name: this.$route.name,
          params: { id: c, folderId: this.computedFolderId },
          hash: this.$route.hash,
        });
      },
    },
    inputPages() {
      return this.pages.filter((page) => this.isRequired(page.name));
    },
    getJobsInProgress() {
      if (!this.jobs.length) return [];
      return this.jobs.filter((e) => e.status === "IN_PROGRESS" || e.status === "NOT_STARTED");
    },
    isJobInProgress() {
      return this.jobs && !!this.getJobsInProgress.length;
    },
    hasLogReports() {
      return this.jobs.some((job) => job.report_url !== null);
    },
    jobsWithLogReports() {
      return this.jobs.filter((job) => job.report_url !== null);
    },
    validate() {
      return this.ajv.getSchema("case.schema.json");
    },
    currentCaseErrors() {
      const errors = this.caseErrors.reduce((acc, err) => {
        const path = err.dataPath.substring(1).split("/");
        this.$utils.pathBuilder(acc, path, err.message);
        return acc;
      }, {});

      return errors;
    },
    aaAndPCComplete() {
      const aaJob = this.getJob(1);
      const pcJob = this.getJob(2);
      if (aaJob && pcJob && aaJob.status === "COMPLETE" && pcJob.status === "COMPLETE") {
        return true;
      }
      return false;
    },
    dialogErrors() {
      const filteredErrors = this.caseErrors.filter((error) => !["if"].includes(error.keyword));
      if (this.showAllErrors) {
        return filteredErrors;
      }
      return filteredErrors.length > 5 ? filteredErrors.slice(0, 5) : filteredErrors;
    },
    // Required by inputSelectionMixin
    computedCaseSettings() {
      return this.currentCase ? this.currentCase.settings : {};
    },
  },
  watch: {
    $route(to, from) {
      if (from.params.id !== to.params.id) {
        this.init();
      }
    },
  },
  created() {
    this.init();
  },
  beforeDestroy() {
    clearTimeout(this.outputPoll);
    clearTimeout(this.refreshJobs);
  },
  methods: {
    ...mapActions({
      loadCase: "cases/get",
      createJob: "jobs/create",
    }),
    init() {
      this.loadCase(this.case_id)
        .then((res) => {
          if (!this.folderId) {
            this.computedFolderId = res.directory_id;
          }
          if (res.code && res.code === "404") {
            return this.goToFolder(this.computedFolderId);
          }
          this.initCurrentCase();
          return this.loadJobs();
        })
        .catch(() => {
          this.goToFolder(this.computedFolderId);
        });
    },
    initCurrentCase() {
      this.currentCase = this.getCase(this.case_id).clone();

      // Here we update the case structure,
      // adding fields, changing units, etc.
      this.currentCase.input = new CaseModel.Input(this.currentCase.input, {});
      this.currentCase.settings = new CaseModel.Settings(this.currentCase.settings);
      runUpdates(this.currentCase);

      console.debug("Case:", this.currentCase);
    },
    confirmSaveCase() {
      console.debug("Saving", this.currentCase);
      if (!this.validateCase() && !this.invalidCaseDialog) {
        this.invalidCaseDialog = true;
        return;
      }

      this.invalidCaseDialog = false;
      if (this.jobs.length) {
        this.confirmSaveDialog = true;
      } else {
        this.saveCase();
      }
    },
    validateCase() {
      const validateCase = new CaseModel(this.currentCase);
      if (!this.validate(validateCase)) {
        this.caseErrors = this.validate.errors;
        console.error("This case has errors: ", this.validate.errors, this.currentCaseErrors);
        return false;
      }
      this.caseErrors = [];
      return true;
    },
    caseChanged() {
      this.hasChanged = true;
      this.validateCase();
    },
    saveCase() {
      this.currentCase.commit();
      this.currentCase.save().then(() => {
        this.confirmSaveDialog = false;
        this.invalidCaseDialog = false;
        this.hasChanged = false;
        this.initCurrentCase();
        this.loadJobs();
      });
    },
    saveCaseAs(name) {
      this.confirmSaveDialog = false;
      delete this.currentCase.id;
      delete this.currentCase.created;
      delete this.currentCase.updated;
      delete this.currentCase.jobs;

      this.currentCase.name = name;
      this.currentCase
        .save()
        .then((c) => {
          this.case_id = c.id;
          return c;
        })
        .then((_case) => {
          this.$store.dispatch("scenarios/get", _case.scenario_id);
        });
    },
    removeCase() {
      this.$store.dispatch("cases/confirmRemove", this.case_id).then((res) => {
        if (res) {
          this.gotoScenario(this.scenario_id);
        }
      });
    },
    hashify(str) {
      return `#${str.replace(/ /g, "")}`;
    },
    runJob(type) {
      this.currentType = type;
      const currentJob = this.getJob(type);
      if (currentJob && currentJob.status === "COMPLETE") {
        this.reRunDialog = true;
      } else {
        this.newJob(type);
      }
    },
    reRunJob(type, optType = null) {
      this.newJob(type);
      if (optType) {
        this.newJob(optType);
      }
      this.reRunDialog = false;
    },
    removeOldJob(type) {
      const oldJob = this.getJob(type);
      if (oldJob) {
        return this.$store.dispatch("jobs/remove", oldJob.id);
      }
      return Promise.resolve();
    },
    newJob(type) {
      this.removeOldJob(type).then(() => {
        this.createJob({
          case_id: this.case_id,
          status: "NOT_STARTED",
          type,
        }).then(() => {
          this.outputPoll = setTimeout(this.loadJobs, 2 * 1000);
        });
      });
    },
    getJob(type) {
      if (this.jobs) {
        return this.jobs.find((e) => e.type === type);
      }
      return null;
    },
    cancelJobsInProgress() {
      this.getJobsInProgress.forEach((element) => {
        this.$store.dispatch("jobs/remove", element.id);
      });
      this.loadJobs();
    },
    handleClickJob(type) {
      const status = this.getJobStatus(type);
      switch (status) {
        case "NOT_STARTED":
        case "IN_PROGRESS":
          this.removeOldJob(type);
          break;
        case "COMPLETE":
          this.currentType = type;
          this.reRunDialog = true;
          break;
        case "COMPLETE_WITH_WARNINGS":
          this.currentWarnings = this.getJobWarnings(type);
          this.currentType = type;
          this.warningsDialog = true;
          break;
        case "ERROR":
          this.newJob(type);
          break;
        default:
          this.newJob(type);
      }
    },
    getJobStatus(type) {
      const job = this.getJob(type);
      return job ? job.status : null;
    },
    getJobWarnings(type) {
      const job = this.getJob(type);
      return job ? job.warnings : null;
    },
    getJobError(type) {
      const job = this.getJob(type);
      return job ? job.error : null;
    },
    getJobProgress(type) {
      const job = this.getJob(type);
      return job ? job.progress : null;
    },
    goToFolder(folderId) {
      this.$router.push({
        name: "Folders",
        params: { folderId },
      });
    },
    gotoScenario(id) {
      this.$router.push({
        name: "ScenarioCases",
        params: { id },
      });
    },
    jobStartable({ type }) {
      const job = this.getJob(type);
      if (job) {
        return ["NOT_STARTED", "ERROR", "COMPLETE", "COMPLETE_WITH_WARNINGS"].includes(job.status);
      }
      return true;
    },
    loadJobs() {
      console.debug("Loading Jobs");
      return this.$store
        .dispatch("jobs/find", {
          paginate: false,
          query: { case_id: this.case_id },
        })
        .then((res) => {
          console.debug("Jobs Found", res);
          if (!res.total) {
            this.$store.commit("jobs/clearAll");
          } else if (this.isJobInProgress) {
            // 15 seconds
            this.refreshJobs = setTimeout(this.loadJobs, 15 * 1000);
          }
        });
    },
    mapTypeName(type, short = null) {
      if (type) {
        return short ? this.jobTypes.get(type).shortName : this.jobTypes.get(type).longName;
      }
      return "";
    },
    parseError(error) {
      const { dataPath, message, schemaPath } = error;
      let path = dataPath.substring(1).replace(/\//g, " > ").replace(/_/g, " ");
      try {
        const propertyTitle = this.schemaPropertyTitle(schemaPath);
        if (propertyTitle) {
          path = path.replace(dataPath.split("/")[dataPath.split("/").length - 1], propertyTitle);
        }
        return `${path}: ${message}`;
      } catch (err) {
        return `${path}: ${message}`;
      }
    },
    schemaPropertyTitle(schemaPath) {
      const [schemaId, ...path] = schemaPath.split("/");
      try {
        const { schema } = this.ajv._schemas[schemaId];
        const property = pointer.get(
          schema,
          `/${path.join("/").slice(0, path.join("/").lastIndexOf("/"))}`
        );
        if (property) {
          return property.title || "";
        }
      } catch (_error) {
        return "";
      }
      return "";
    },
    /**
     * Allows to download the log report of the current case
     * depending on the algorithm type
     * @param {string} url: correspond to the url of the report
     */
    downloadLogReport(url) {
      window.open(url);
    },
  },
};
</script>

<style scoped>
.error-item {
  min-height: 24px;
}
.text-clickable {
  cursor: pointer;
}
.text-clickable:hover {
  cursor: pointer;
  color: black;
}
</style>
